import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import Tippy from '@tippyjs/react';
import { ToolMenu } from 'app/components';

import { ConfirmationModal } from 'app/components';
import { useDeleteReferenceData } from 'entities/reference-data';
import { Clickable } from 'shared/ui/Clickable';
import { SelectLanguageModal } from 'shared/overlays/LanguageModal';
import type { ReferenceListEntry } from './ReferenceData';
import { ApiError } from 'shared/api/client';
import { usePermissions } from 'app/api/permission-queries';
import { showTaggerWarning, tableHasTags } from '../update-modal/updatePermissions';

interface ReferenceDataDropdownMenuProps {
  documentId: string;
  onChangeName: (item: ReferenceListEntry) => void;
  item: ReferenceListEntry;
  setShowDetails: (show: boolean) => void;
  onLocaleChange: (item: ReferenceListEntry, locale: string) => void;
}

const ReferenceDataDropdownMenu = ({ documentId, onChangeName, item, setShowDetails, onLocaleChange }: ReferenceDataDropdownMenuProps) => {
  const { t } = useTranslation(['common', 'reference-data']);

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showLocalModal, setShowLocaleModal] = useState(false);
  const [visble, setVisible] = useState(false);
  const [referenceInUseError, setReferenceInUseError] = useState(false);
  const { mutate: deleteReferenceData, isPending: isLoading } = useDeleteReferenceData();
  const { documentPermissions } = usePermissions(documentId);
  const taggerWarning = showTaggerWarning(tableHasTags(item?.selectionContent), documentPermissions);

  const navigate = useNavigate();

  const showTippy = () => setVisible(true);
  const hideTippy = () => setVisible(false);

  const toggleConfirmationModal = () => {
    setShowDeleteConfirmationModal(!showDeleteConfirmationModal);
    hideTippy();
  };

  const deleteReference = () => {
    deleteReferenceData(
      {
        documentId,
        referenceData: item,
      },
      {
        onSuccess: () => {
          navigate({ to: '/documents/$documentId/reference-data', params: { documentId } });
        },
        onSettled: () => {
          setShowDetails(false);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === 'REFERENCE_IN_USE') {
            setReferenceInUseError(true);
          }
        },
      }
    );
  };

  const toggleLocaleModal = () => {
    hideTippy();
    setShowLocaleModal(!showLocalModal);
  };

  const handleClickChangeName = () => {
    onChangeName(item);
    hideTippy();
  };
  const handleLocaleChange = (locale: string) => {
    onLocaleChange(item, locale);
    setShowLocaleModal(false);
  };
  const referenceMenuItems = (_item: ReferenceListEntry) => [
    { text: t('reference-data:item.dropdown.edit-name'), icon: 'fa-regular fa-pen', onClick: () => handleClickChangeName() },
    { text: t('reference-data:item.dropdown.edit-lang'), icon: 'fa-regular fa-globe', onClick: toggleLocaleModal },
    { text: t('reference-data:item.dropdown.delete'), icon: 'fa-regular fa-trash-can', onClick: toggleConfirmationModal },
  ];

  return (
    <>
      <Tippy visible={visble} interactive={true} offset={[-90, -110]} onClickOutside={hideTippy} content={<ToolMenu menuItems={referenceMenuItems(item)} />}>
        <Clickable onClick={showTippy}>
          <i className="fa-regular fa-ellipsis-v p-2" onClick={showTippy} />
        </Clickable>
      </Tippy>
      <ConfirmationModal
        message={
          <>
            <i className="fa-regular fa-trash-can fs-1 mb-4" />
            <h3 className="confirm-delete-header">
              {t('reference-data:item.delete.confirm-title')}: {item.name}
            </h3>
            <span className="text-gray-900 fs-7">{t('reference-data:item.delete.confirm-text')}</span>
            {taggerWarning && (
              <div className="compare-permission-notice compare-permission-notice--inline text-start">{t('reference-data:review-tags.no-tag-permission')}</div>
            )}
            {referenceInUseError && <div className="mt-3 text-red-500 fs-7">{t('reference-data:item.delete.in-use-error')}</div>}
          </>
        }
        title=" "
        show={showDeleteConfirmationModal}
        onConfirm={deleteReference}
        onDecline={toggleConfirmationModal}
        busy={isLoading}
      />
      <SelectLanguageModal
        title={t('reference-data:xliff.source-lang')}
        show={showLocalModal}
        onClose={() => setShowLocaleModal(false)}
        onSubmit={handleLocaleChange}
        currentLocale={item.locale ?? 'sv-SE'}
        confirmText={t('common:confirm')}
      />
    </>
  );
};

export default ReferenceDataDropdownMenu;
